<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  MAX_BACKGROUND_IMAGES,
  MAX_FILE_BYTES,
  MAX_FILE_MB,
} from '@/helpers/RobvisionConstants';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

// Selected background images. 0 files is a valid state — the upload is optional.
const files = defineModel<File[]>({ default: () => [] });
const valid = defineModel<boolean>('valid', { default: true });

const stagingFiles = ref<File[]>([]);

function onStagingPicked(picked: File | File[] | null) {
  // v-file-input with `multiple` resolves to File[] (or null when cleared).
  const list = Array.isArray(picked) ? picked : picked ? [picked] : [];
  if (list.length > 0) {
    files.value = [...files.value, ...list];
  }
  // reset the picker so the same control can append more files
  stagingFiles.value = [];
}

function removeAt(index: number) {
  const next = [...files.value];
  next.splice(index, 1);
  files.value = next;
}

// Thumbnails via object URLs, revoked whenever the file list changes.
const previews = ref<string[]>([]);
watch(
  files,
  (newFiles) => {
    previews.value.forEach((url) => URL.revokeObjectURL(url));
    previews.value = newFiles.map((file) => URL.createObjectURL(file));
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  previews.value.forEach((url) => URL.revokeObjectURL(url));
});

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const validationErrors = computed<string[]>(() => {
  const errors: string[] = [];
  if (files.value.length > MAX_BACKGROUND_IMAGES) {
    errors.push(
      $gettext('Maximum %{n} background images allowed.', {
        n: String(MAX_BACKGROUND_IMAGES),
      }),
    );
  }
  if (files.value.some((f) => f.size > MAX_FILE_BYTES)) {
    errors.push(
      $gettext('Some files exceed %{n} MB.', { n: String(MAX_FILE_MB) }),
    );
  }
  return errors;
});

watch(
  validationErrors,
  (errors) => {
    valid.value = errors.length === 0;
  },
  { immediate: true },
);
</script>

<template>
  <div class="custom-backgrounds-input">
    <div class="text-subtitle-2">
      {{ $gettext('Backgrounds of your deployment environment (optional)') }}
    </div>
    <p class="text-caption text-medium-emphasis mb-2">
      {{ $gettext('Photos of the surface or area where the robot will pick.') }}
    </p>
    <div class="upload-controls">
      <v-file-input
        v-model="stagingFiles"
        multiple
        accept=".png,.jpg,.jpeg"
        prepend-icon="mdi-image-multiple"
        density="compact"
        hide-details
        :clearable="false"
        :disabled="files.length >= MAX_BACKGROUND_IMAGES"
        :label="
          files.length === 0
            ? $gettext('Select background images')
            : $gettext('Add more background images')
        "
        class="upload-picker"
        @update:model-value="onStagingPicked"
      />
      <span class="counter text-caption text-medium-emphasis">
        {{
          $gettext('%{n} of %{max} images', {
            n: String(files.length),
            max: String(MAX_BACKGROUND_IMAGES),
          })
        }}
      </span>
    </div>

    <v-alert
      v-for="(err, i) in validationErrors"
      :key="`bg-err-${i}`"
      type="warning"
      density="compact"
      variant="tonal"
      class="mt-2"
    >
      {{ err }}
    </v-alert>

    <div v-if="files.length > 0" class="image-list mt-2">
      <div
        v-for="(file, index) in files"
        :key="`${file.name}-${index}`"
        class="image-card"
      >
        <img :src="previews[index]" class="image-thumb" alt="" />
        <div class="image-meta">
          <div class="image-name" :title="file.name">
            {{ file.name }}
          </div>
          <div class="text-caption text-medium-emphasis">
            {{ formatBytes(file.size) }}
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="$gettext('Remove image')"
          @click="removeAt(index)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.upload-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-picker {
  flex: 1 1 auto;
}

.counter {
  flex: 0 0 auto;
}

.image-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 8px;
  max-height: 30vh;
  overflow-y: auto;
}

.image-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.image-thumb {
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.image-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.image-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
